<template>
  <nav ref="el" class="nav">
    <!-- {{ blok.menu_secondary_links }}
    {{ blok.menu_tertiary_links }} -->
    <WaveSvg class="wave" />

    <div class="container-full">
      <div class="primary-links">
        <ul class="grid">
          <li v-for="(item, index) in blok.menu_primary_links" :key="'primary' + index" class="primary-link-group">
            <SharedBaseLink
              :link="item.link"
              class="primary-link heading-main-xlarge"
              :class="{ 'is-active': linkIsActive(item.link) }">
              {{ item.text }}
            </SharedBaseLink>
            <span class="heading-subline">
              {{ item.subline }}
            </span>
          </li>
        </ul>
      </div>

      <div class="bottom-links grid">
        <div class="secondary-links">
          <div v-for="(column, index) in secondaryColumns" :key="'secondary-col-' + index" class="column">
            <SharedBaseLink
              v-for="linkItem in column"
              :key="linkItem._uid"
              :link="linkItem.link"
              :underlined="true"
              class="bottom-link link-secondary"
              :class="{ 'is-active': linkItem.link && linkIsActive(linkItem.link) }">
              <span class="action-large-bold">{{ linkItem.text }} </span>
            </SharedBaseLink>
          </div>
        </div>

        <!-- Tertiary Links & Market Select  -->
        <div class="tertiary-links">
          <BMMarketSelector />
          <SharedBaseLink
            v-for="item in blok.menu_tertiary_links"
            :key="item._uid"
            :link="item.link"
            :underlined="true"
            class="bottom-link link-tertiary"
            :class="{ 'is-active': item.link && linkIsActive(item.link) }">
            <span class="action-large-regular">{{ item.text }} </span>
          </SharedBaseLink>
        </div>
      </div>
    </div>
  </nav>
</template>
<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import type { ISbAlternateObject } from '@storyblok/vue';
import { SharedBaseLink } from '@seb-platform/shared/components/base';
import { useTransition } from '@seb-platform/shared/composables';
import { normalizeUrl, urlExtractFromLink } from '@seb-platform/shared/utils/url';
import WaveSvg from '~/assets/svg/schaerer_wave_large.svg?skipsvgo';
import type { BmHeaderInPageStoryblok, BmHeaderRichStoryblok, MultilinkStoryblok } from '~/types/storyblok-generated';
import BMMarketSelector from '../BMMarketSelector/BMMarketSelector.vue';

const props = defineProps<{
  blok: BmHeaderRichStoryblok;
  inPageBlok?: BmHeaderInPageStoryblok[];
  alternates?: ISbAlternateObject[];
}>();

const $el = useTemplateRef('el');

const { fadeSlide } = useTransition();
const { currentRoute } = useRouter();

function linkIsActive(link: MultilinkStoryblok) {
  if (!link) {
    return false;
  }
  const url = urlExtractFromLink(link) ?? '';
  const normalizedUrl = normalizeUrl(url);
  const currentUrl = normalizeUrl(currentRoute.value.fullPath);
  return currentUrl.startsWith(normalizedUrl);
}

const secondaryColumns = computed(() => {
  const maxItemsPerColumn = 4;
  const result = [];

  if (!props.blok.menu_secondary_links) return [];
  for (let i = 0; i < props.blok.menu_secondary_links.length; i += maxItemsPerColumn) {
    result.push(props.blok.menu_secondary_links.slice(i, i + maxItemsPerColumn));
  }

  return result;
});

onMounted(() => {
  const isMobile = window.innerWidth < 1024;

  if ($el.value)
    fadeSlide($el.value, {
      direction: 'y',
      offsetMultiplier: -1,
      speedMultiplier: 0.5,
      alphaSpeedMultiplier: 0.25,
    });

  const linkItems = $el.value
    ? Array.from($el.value.querySelectorAll('.primary-link-group') as NodeListOf<HTMLElement>)
    : [];

  fadeSlide(linkItems, {
    direction: 'x',
    baseDelay: 0.25,
    offsetMultiplier: -1,
    speedMultiplier: 1,
    alphaSpeedMultiplier: 0.5,
  });

  const secondaryLinks = $el.value
    ? Array.from($el.value.querySelectorAll('.link-secondary') as NodeListOf<HTMLElement>)
    : [];

  fadeSlide(secondaryLinks, {
    direction: 'x',
    baseDelay: isMobile ? 0.8 : 1.2,
    offsetMultiplier: -1,
    speedMultiplier: 1,
    alphaSpeedMultiplier: 0.5,
  });

  const tertiaryLinks = $el.value
    ? Array.from($el.value.querySelectorAll('.link-tertiary') as NodeListOf<HTMLElement>)
    : [];

  const marketSelector = $el.value?.querySelector('.market-selector');
  if (marketSelector) {
    if (!isMobile) {
      tertiaryLinks.unshift(marketSelector as HTMLElement);
    } else {
    }
  }

  fadeSlide(tertiaryLinks, {
    direction: 'x',
    baseDelay: isMobile ? 1.2 : 0.8,
    offsetMultiplier: -1,
    speedMultiplier: 1,
    alphaSpeedMultiplier: 0.5,
  });
});
</script>
<style src="./BMNavLayer.scss" lang="scss" scoped />
